import React, { useState } from "react";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import CustomTable from "../../components/Common/CustomTable";
import useApi from "../../Hooks/useApi";
import { getDepositList, downloadForm12 } from "../../helpers/API";
import useFilter from "../../Hooks/useFilter";
import NewDepositModal from "./NewDepositModal";
import callApi from "../../helpers/CallApi";
import { toast } from "react-toastify";
import { DATE_FORMAT } from "../../helpers/dateFormat";

const List = ({ t }) => {
  const [filters, setFilters] = useFilter({ sortField: "deposit.createdAt", sort: "desc" });
  const [depositTypeFilter, setDepositTypeFilter] = useState("all");
  const [showNewModal, setShowNewModal] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [data] = useApi({
    callback: getDepositList,
    payload: {
      ...filters,
      paymentType:
        depositTypeFilter !== "all" ? depositTypeFilter : undefined,
    },
    dependencies: [filters, depositTypeFilter, refresh],
  });

  const depositTypeFilters = [
    { key: "all", label: t("deposit.list.filters.all"), variant: "primary" },
    { key: "cash", label: t("deposit.list.filters.cash"), variant: "success" },
    { key: "bank", label: t("deposit.list.filters.bank"), variant: "info" },
  ];

  const columns = [
    {
      name: t("deposit.list.columns.date"),
      dateField: "paymentDate",
      sortable: true,
      sortField: "paymentDate",
      dateFormat: DATE_FORMAT.DISPLAY,
      width: "110px",
    },
    {
      name: t("deposit.list.columns.receiptNumber"),
      selector: (row) => (
        <span className="fw-semibold text-primary">
          {row.receiptNumber || row.receiptNo || "—"}
        </span>
      ),
      sortable: true,
      sortField: "receiptNumber",
      width: "130px",
    },
    {
      name: t("deposit.list.columns.name"),
      selector: (row) => (
        <span className="fw-medium">
          {row.borrowerName || row.borrowerName || "—"}
        </span>
      ),
      sortable: true,
      sortField: "borrowerName",
    },
    {
      name: t("deposit.list.columns.interestAmount"),
      selector: (row) => (
        <span className="fw-semibold">
          ₹{Number(row.interestAmount || row.interestAmountPaid || 0).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
      sortField: "interestAmount",
      width: "130px",

    },
    {
      name: t("deposit.list.columns.principalAmount"),
      selector: (row) => (
        <span className="fw-semibold">
          ₹{Number(row.principalAmount || row.principalAmountPaid || 0).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
      sortField: "principalAmount",
      width: "140px",

    },
    {
      name: t("deposit.list.columns.totalAmount"),
      selector: (row) => (
        <span className="fw-semibold">
          ₹{Number(row.totalAmount || row.totalAmountPaid || 0).toLocaleString("en-IN")}
        </span>
      ),
      sortable: true,
      sortField: "totalAmount",
      width: "130px",

    },
    {
      name: t("deposit.list.columns.paymentType"),
      selector: (row) => {
        const type = row.paymentType || "cash";
        const isBank = type === "bank";
        return (
          <span
            className={`badge ${isBank ? "bg-info" : "bg-success"} rounded-pill px-2 py-1`}
            style={{ fontSize: "11px" }}
          >
            {isBank ? t("deposit.list.paymentType.bank") : t("deposit.list.paymentType.cash")}
          </span>
        );
      },
      sortable: true,
      sortField: "paymentType",
      width: "100px",
      center: true,
    },
    {
      name: t("deposit.list.columns.bankName"),
      selector: (row) =>
        row.paymentType === "bank" ? row.bankName || "—" : "—",
      width: "160px",
    },
    {
      name: t("deposit.list.columns.chequeNumber"),
      selector: (row) =>
        row.paymentType === "bank" ? row.chequeNumber || "—" : "—",
      width: "150px",
    },
    {
      name: t("deposit.list.columns.actions"),
      actionItems: [
        {
          label: t("deposit.list.actions.downloadForm12"),
          icon: "ri-file-download-line",
          className: "btn btn-outline-primary btn-sm",
          onClick: (row) => handleDownloadForm12(row),
        },
      ],
      width: "90px",
      center: true,
    },
  ];

  const filterButtons = (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      <span className="text-muted small fw-medium me-1">
        {t("deposit.list.filters.filterBy")}:
      </span>
      {depositTypeFilters.map(({ key, label, variant }) => (
        <button
          key={key}
          type="button"
          className={`btn btn-sm ${depositTypeFilter === key
            ? `btn-${variant} shadow-sm`
            : `btn-outline-${variant}`
            }`}
          style={{ fontSize: "12px", padding: "4px 12px" }}
          onClick={() => setDepositTypeFilter(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const handleDepositSaved = () => {
    setRefresh((prev) => !prev);
    setShowNewModal(false);
  };

  const handleDownloadForm12 = (row) => {
    if (!row?.id) return;
    callApi({
      callback: downloadForm12,
      payload: [row.id],
      onSuccess: (res) => {
        const blobUrl = window.URL.createObjectURL(res);
        const disposition = res?.headers?.["content-disposition"];
        let filename = "form-12.pdf";
        if (disposition) {
          const match = disposition.match(/filename="(.+)"/);
          if (match?.[1]) filename = match[1];
        }
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(blobUrl);
      },
      onError: (error) => toast.error(error || t("deposit.list.messages.downloadError")),
    });
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <CustomTable
            columns={columns}
            data={data}
            buttonAction={() => setShowNewModal(true)}
            buttonLabel={t("deposit.list.button.newEntry")}
            buttonIcon="bx bx-plus"
            onStatusChange={(data) => setFilters(data)}
            searchKeys={["receiptNumber", "borrowerName"]}
            searchPlaceholder={t("deposit.list.search.placeholder")}
            headerContent={filterButtons}
          />
        </Col>
      </Row>
      <NewDepositModal
        show={showNewModal}
        toggle={() => setShowNewModal(false)}
        onSaved={handleDepositSaved}
      />
    </React.Fragment>
  );
};

export default withTranslation()(List);
